import {
	Autocomplete,
	Box,
	CircularProgress,
	FormControl,
	Grid,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { countries } from "utils/Countries";
import { useTranslation } from "react-i18next";
import type { UseEmployeeFormReturn } from "./hooks/useEmployeeForm";

interface EmployeeFormFieldsProps {
	form: UseEmployeeFormReturn;
}

const EmployeeFormFields = ({ form }: EmployeeFormFieldsProps) => {
	const { t } = useTranslation();

	const {
		firtsName, setFirtsName,
		lastName, setLastName,
		birthDate, setBirthDate,
		email, setEmail,
		phoneNumber, setPhoneNumber,
		address, setAddress,
		documentEmployee, setDocumentEmployee,
		payRule, setPayRule,
		documentType, setDocumentType,
		country, setCountry,
		timezone, setTimezone,
		payRules,
		documentTypes,
		timezones,
		loadingPayRule,
		loadingDocumentType,
		existPersonNumber,
	} = form;

	return (
		<Grid container spacing={2}>
			<Grid size={6}>
				<TextField
					fullWidth
					required
					name="first_name"
					label={t("timekeeper.employees.dialog.add.name")}
					value={firtsName}
					onChange={(e) => setFirtsName(e.target.value)}
					slotProps={{
						htmlInput: { maxLength: 100 },
						input: {
							endAdornment: (
								<InputAdornment position="end">{`${firtsName.length}/100`}</InputAdornment>
							),
						},
					}}
				/>
			</Grid>

			<Grid size={6}>
				<TextField
					fullWidth
					required
					name="last_name"
					label={t("timekeeper.employees.dialog.add.last_name")}
					value={lastName}
					onChange={(e) => setLastName(e.target.value)}
					slotProps={{
						htmlInput: { maxLength: 100 },
						input: {
							endAdornment: (
								<InputAdornment position="end">{`${lastName.length}/100`}</InputAdornment>
							),
						},
					}}
				/>
			</Grid>

			<Grid size={5}>
				<FormControl fullWidth size="medium">
					<InputLabel id="type-of-document-label">
						{t("timekeeper.employees.dialog.add.type_of_document")} *
					</InputLabel>
					<Select
						disabled={loadingDocumentType}
						labelId="type-of-document-label"
						required
						id="type-of-document"
						value={String(documentType)}
						label={t("timekeeper.employees.dialog.add.type_of_document")}
						onChange={(e: SelectChangeEvent) => setDocumentType(e.target.value)}
						IconComponent={(props) =>
							loadingDocumentType ? (
								<CircularProgress size={18} {...props} sx={{ mr: 0.5 }} />
							) : (
								<ArrowDropDownIcon {...props} />
							)
						}
					>
						{documentTypes.map((td) => (
							<MenuItem value={td.id} key={td.id}>
								{td.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>

			<Grid size={7}>
				<TextField
					fullWidth
					required
					name="document_employee"
					label={t("timekeeper.employees.dialog.add.document_number")}
					value={documentEmployee}
					onChange={(e) => setDocumentEmployee(e.target.value)}
					slotProps={{
						htmlInput: { maxLength: 30 },
						input: {
							endAdornment: (
								<InputAdornment position="end">{`${documentEmployee.length}/30`}</InputAdornment>
							),
						},
					}}
					error={existPersonNumber()}
				/>
			</Grid>

			<Grid size={4}>
				<Autocomplete
					id="country-select"
					fullWidth
					options={countries}
					autoHighlight
					getOptionLabel={(option) => `${option.code} +(${option.phone})`}
					value={country}
					onChange={(_, newValue) => setCountry(newValue)}
					renderOption={(props, option) => {
						const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
						return (
							<Box
								key={key}
								component="li"
								sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
								{...optionProps}
							>
								<img
									loading="lazy"
									width="20"
									srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
									src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
									alt=""
								/>
								{option.label} ({option.code}) +{option.phone}
							</Box>
						);
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label={t("timekeeper.employees.dialog.add.national_code")}
							slotProps={{
								htmlInput: {
									...params.inputProps,
									autoComplete: "new-password",
								},
							}}
						/>
					)}
				/>
			</Grid>

			<Grid size={4}>
				<TextField
					fullWidth
					required
					name="phone_number"
					label={t("timekeeper.employees.dialog.add.phone")}
					value={phoneNumber}
					onChange={(e) => setPhoneNumber(e.target.value)}
					type="tel"
					slotProps={{
						htmlInput: { maxLength: 30, minLength: 4 },
						input: {
							endAdornment: (
								<InputAdornment position="end">{`${phoneNumber.length}/30`}</InputAdornment>
							),
						},
					}}
				/>
			</Grid>

			<Grid size={4}>
				<DatePicker
					label={t("timekeeper.employees.dialog.add.birth_date")}
					name="birth_date"
					onChange={(value) => setBirthDate(value)}
					value={birthDate}
					slotProps={{
						textField: { required: true, fullWidth: true },
					}}
				/>
			</Grid>

			<Grid size={12}>
				<FormControl fullWidth required>
					<InputLabel id="timezone-label">
						{t("timekeeper.employees.dialog.add.timezone")}
					</InputLabel>
					<Select
						labelId="timezone-label"
						id="timezone-select"
						value={timezone}
						label={t("timekeeper.employees.dialog.add.timezone")}
						onChange={(e: SelectChangeEvent) => setTimezone(e.target.value)}
						MenuProps={{ style: { maxHeight: 400 } }}
					>
						{timezones.map((tz) => (
							<MenuItem key={tz.value} value={tz.value}>
								{tz.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>

			<Grid size={12}>
				<TextField
					fullWidth
					required
					name="email"
					label={t("timekeeper.employees.dialog.add.email")}
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					type="email"
					slotProps={{
						htmlInput: { maxLength: 100 },
						input: {
							endAdornment: (
								<InputAdornment position="end">{`${email.length}/100`}</InputAdornment>
							),
						},
					}}
				/>
			</Grid>

			<Grid size={12}>
				<FormControl fullWidth size="medium">
					<InputLabel id="payrule-select-label">
						{t("timekeeper.employees.dialog.add.select_pay_rule")}
					</InputLabel>
					<Select
						labelId="payrule-select-label"
						required
						id="payrule-select"
						value={String(payRule)}
						label={t("timekeeper.employees.dialog.add.select_pay_rule")}
						onChange={(e: SelectChangeEvent) => setPayRule(e.target.value)}
						disabled={loadingPayRule}
						IconComponent={(props) =>
							loadingPayRule ? (
								<CircularProgress size={18} {...props} sx={{ mr: 0.5 }} />
							) : (
								<ArrowDropDownIcon {...props} />
							)
						}
					>
						{payRules.map((pr) => (
							<MenuItem value={pr.pay_rule_id} key={"payrule-" + pr.pay_rule_id}>
								{pr.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>

			<Grid size={12}>
				<TextField
					fullWidth
					required
					name="address"
					label={t("timekeeper.employees.dialog.add.address")}
					value={address}
					onChange={(e) => setAddress(e.target.value)}
					slotProps={{
						htmlInput: { maxLength: 100, minLength: 4 },
						input: {
							endAdornment: (
								<InputAdornment position="end">{`${address.length}/100`}</InputAdornment>
							),
						},
					}}
					error={address.length > 0 && address.length < 4}
				/>
			</Grid>
		</Grid>
	);
};

export { EmployeeFormFields };
